import { Selection } from 'components/DataTable';
import { FilterState } from 'components/DataTable/DataTable';
import { Form } from 'components/Form/base/Form';
import { FormInput, FormSubmit } from 'components/Form/next';
import Loader from 'components/Loader';
import { omit } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import useSWR from 'swr';
import { useApi } from '../../../hooks/useApi';

interface Props {
    filter?: FilterState;
    clubSlug: string;
    selection: Selection;
    callback?: () => void;
}

export const GroupAssignmentForm = ({ filter, clubSlug, selection, callback }: Props) => {
    const { t } = useTranslation('member-management');
    const api = useApi();
    const { data, error } = useSWR(`club-member/${clubSlug}/group`);
    const [currentGroup, setCurrentGroup] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const removeAssignment = () => {
        setIsLoading(true);
        return api({
            url: `club-member/${clubSlug}/group`,
            method: 'PUT',
            data: { members: selection, filter },
            onBefore: () => setIsLoading(false),
            onSuccess: () => callback?.()
        });
    };

    const submitHandler = (data) => {
        const isNewGroup = String(data.id).includes('new-id');
        setIsLoading(true);
        return api({
            url: isNewGroup ? `club-member/${clubSlug}/group` : `club-member/${clubSlug}/group/${data.id}`,
            method: isNewGroup ? 'POST' : 'PUT',
            data: { ...omit(data, ['id']), members: selection, filter },
            onBefore: () => setIsLoading(false),
            onSuccess: () => callback?.()
        });
    };

    return (
        <Loader isLoading={!data && !error}>
            <div className="tw-grid tw-grid-cols-1 tw-justify-between tw-gap-1">
                <div className="tw-grid tw-w-full">
                    <Typeahead
                        positionFixed
                        id="club_member_group_selection"
                        onChange={([selected]: any[]) => {
                            setCurrentGroup({ ...selected, title: selected?.label || selected?.title });
                        }}
                        options={data?.data}
                        emptyLabel={t('groups.assignmentForm.noGroupFound')}
                        newSelectionPrefix={t('groups.assignmentForm.newGroupPrefix')}
                        labelKey={(option: any) => option?.title}
                        allowNew={true}
                    />
                </div>
                <div className="tw-grid tw-w-full">
                    {currentGroup ? (
                        <Form onSubmit={submitHandler} defaultValues={currentGroup}>
                            <FormInput name="title" label={t('groups.assignmentForm.title')} />
                            <FormInput type="color" name="color" label={t('groups.assignmentForm.color')} />
                            <FormSubmit isLoading={isLoading} css={{ justifySelf: 'end' }} />
                        </Form>
                    ) : (
                        <FormSubmit type="button" isLoading={isLoading} color="error" onPress={removeAssignment}>
                            {t('groups.assignmentForm.removeAssignment')}
                        </FormSubmit>
                    )}
                </div>
            </div>
        </Loader>
    );
};

export default GroupAssignmentForm;
